import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { envConfig } from '../config/environment';
import { FolderNode } from '../../pages/protected/data-exchange/features/upload-files/folder-tree/folder-tree';
import { UploadTaskPayload } from '../upload-task/upload-task-service';
import { LoggerService } from '../logging/logger.service';

/** Backend payload item for OEM and its available project list.
 * - `oem` is the name of the OEM.
 * - `project` is an array of project names associated with the OEM.
 */
export interface OemProject {
    oem: string;
    project: string[];
}

export interface ProviderEntry {
    providerAlias: string;
    providerType: 'Internal' | 'External';
}

export interface TestXplorerResponse {
    Testname: string;
    DUTs: string[];
    folder_structure: FolderNode[];
}

export interface UserEntry {
    Username: string;
  'Custom:Role': string | null;
}

export interface UserRoleChangeRequest {
    username: string;
    role: string;
    userpool_id: string;
}

export interface UserProviderAssignment {
  username: string;
  providerAlias: string;
}

@Injectable({
  providedIn: 'root',
})
export class DataExchangeApiService {

    private readonly http = inject(HttpClient);
    private readonly logger = inject(LoggerService);

    private readonly oemProjectEntriesUrl = `${envConfig.apiUrl}/oem-project-entries`;
    private readonly currentProviderEntriesUrl = `${envConfig.apiUrl}/current-provider-entries`;
    private readonly testXplorerGetFolderUrl = `${envConfig.apiUrl}/get-project-folder-from-testxplorer`;
    private readonly getFoldersUrl = `${envConfig.apiUrl}/get-project-folder`;
    private readonly createUploadTaskUrl = `${envConfig.apiUrl}/create-upload-task`;
    private readonly saveOemProjectEntriesUrl = `${envConfig.apiUrl}/create-new-oem-project-entry`;
    private readonly saveProviderEntriesUrl = `${envConfig.apiUrl}/create-new-tdl-entry`;
    private readonly manageUserPoolRoles = `${envConfig.apiUrl}/manage-user-pool-roles-int-ext`;
    private readonly showUploadedFiles = `${envConfig.apiUrl}/show-uploaded-files`;

    async getUploadedFiles(oem: string, project: string): Promise<FolderNode[]> {
        this.logger.debug('DataExchangeApiService', 'Fetching uploaded files for selected project');
        try {
            const response = await firstValueFrom(this.http.post<{ message: FolderNode[] }>(this.showUploadedFiles, { oem, project }));
            this.logger.debug('DataExchangeApiService', 'Uploaded files fetched successfully', { count: response.message?.length || 0 });
            return response.message ?? [];
        } catch (error) {
            this.logger.error('DataExchangeApiService', 'Failed to fetch uploaded files', error);
            throw error;
        }
    }

    async getOemProjects(): Promise<OemProject[]> {
        this.logger.info('DataExchangeApiService', 'Fetching OEM projects');
        try {
            const response = await firstValueFrom(this.http.get<{ message: OemProject[] }>(this.oemProjectEntriesUrl));
            this.logger.debug('DataExchangeApiService', 'OEM projects fetched', { count: response.message?.length || 0 });
            return response.message ?? [];
        } catch (error) {
            this.logger.error('DataExchangeApiService', 'Failed to fetch OEM projects', error);
            throw error;
        }
    }

    async saveOemProjects(entries: OemProject[]): Promise<void> {
      this.logger.info('DataExchangeApiService', 'Saving OEM projects', { count: entries.length });
      try {
        await firstValueFrom(this.http.post(this.saveOemProjectEntriesUrl, { entries }));
        this.logger.info('DataExchangeApiService', 'OEM projects saved successfully', { count: entries.length });
      } catch (error) {
        this.logger.error('DataExchangeApiService', 'Failed to save OEM projects', error);
        throw error;
      }
    }

    async getProviders(): Promise<ProviderEntry[]> {
      this.logger.info('DataExchangeApiService', 'Fetching providers');
      try {
        const response = await firstValueFrom(
          this.http.get<{ message: ProviderEntry[] }>(this.currentProviderEntriesUrl, {
            params: new HttpParams().set('providerType', 'both'),
          })
        );
        this.logger.debug('DataExchangeApiService', 'Providers fetched', { count: response.message?.length || 0 });
        return response.message ?? [];
      } catch (error) {
        this.logger.error('DataExchangeApiService', 'Failed to fetch providers', error);
        throw error;
      }
    }

    async saveProviders(entries: ProviderEntry[]): Promise<void> {
      this.logger.info('DataExchangeApiService', 'Saving providers', { count: entries.length });
      try {
        await firstValueFrom(this.http.post(this.saveProviderEntriesUrl, { entries }));
        this.logger.info('DataExchangeApiService', 'Providers saved successfully', { count: entries.length });
      } catch (error) {
        this.logger.error('DataExchangeApiService', 'Failed to save providers', error);
        throw error;
      }
    }

    async getTestXplorerFolder(toId: string | null): Promise<TestXplorerResponse> {
      this.logger.debug('DataExchangeApiService', 'Fetching TestXplorer folder');
      try {
        const response = await firstValueFrom(this.http.post<{message: TestXplorerResponse}>(this.testXplorerGetFolderUrl, { to_id: toId }));
        this.logger.debug('DataExchangeApiService', 'TestXplorer folder fetched', { rootCount: response.message?.folder_structure?.length ?? 0 });
        return response.message;
      } catch (error) {
        this.logger.error('DataExchangeApiService', 'Failed to fetch TestXplorer folder', error);
        throw error;
      }
    }

    async getProjectFolderTree(): Promise<FolderNode[]> {
      this.logger.info('DataExchangeApiService', 'Fetching project folder tree');
      try {
        const response = await firstValueFrom(
          this.http.get<{ message: FolderNode[] }>(this.getFoldersUrl)
        );
        this.logger.debug('DataExchangeApiService', 'Project folder tree fetched', { count: response.message?.length || 0 });
        return response.message ?? [];
      } catch (error) {
        this.logger.error('DataExchangeApiService', 'Failed to fetch project folder tree', error);
        throw error;
      }
    }

    async createUploadTask(payload: UploadTaskPayload): Promise<any> {
      this.logger.info('DataExchangeApiService', 'Creating upload task', { dutCount: payload.templateInputs.dut.length });
      try {
        const response = await firstValueFrom(this.http.post(this.createUploadTaskUrl, payload));
        this.logger.info('DataExchangeApiService', 'Upload task created successfully');
        return response;
      } catch (error) {
        this.logger.error('DataExchangeApiService', 'Failed to create upload task', error);
        throw error;
      }
    }

    async getAllUsers(): Promise<UserEntry[]> {
      this.logger.debug('DataExchangeApiService', 'Fetching all users from Cognito');
      try {
        const response = await firstValueFrom(
          this.http.get<{ message: UserEntry[] }>(this.manageUserPoolRoles)
        );
        this.logger.debug('DataExchangeApiService', 'Users fetched', { count: response.message?.length || 0 });
        return response.message ?? [];
      } catch (error) {
        this.logger.error('DataExchangeApiService', 'Failed to fetch users', error);
        throw error;
      }
    }

    private async changeUserRole(usersRoleChanges: UserRoleChangeRequest[]): Promise<void> {
      this.logger.debug('DataExchangeApiService', 'Changing user roles', { count: usersRoleChanges.length });
      try {
        await firstValueFrom(this.http.post(this.manageUserPoolRoles, { users: usersRoleChanges }));
        this.logger.debug('DataExchangeApiService', 'User roles changed successfully');
      } catch (error) {
        this.logger.error('DataExchangeApiService', 'Failed to change user roles', error);
        throw error;
      }
    }

    async assignExternalUsersToProviders(userRoleAssignments: UserProviderAssignment[]): Promise<void> {
      this.logger.info('DataExchangeApiService', 'Assigning external users to providers', { count: userRoleAssignments.length });
      try {
        await this.changeUserRole(userRoleAssignments.map((assignment) => ({
          username: assignment.username,
          role: assignment.providerAlias,
          userpool_id: envConfig.userPoolIdExternal,
        })));
        this.logger.info('DataExchangeApiService', 'External users assigned to providers successfully');
      } catch (error) {
        this.logger.error('DataExchangeApiService', 'Failed to assign external users to providers', error);
        throw error;
      }
    }
}
